import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useCampaignList } from "../../hooks/useCampaigns";
import { api } from "../../lib/api";
import { buildBrandReviewPath } from "../../lib/brand-navigation";

const SURFACE_OPTIONS = [
  { value: "blog", label: "Blog" },
  { value: "linkedin_derivative", label: "LinkedIn Derivative" },
  { value: "linkedin_personal", label: "LinkedIn Personal" },
  { value: "reddit", label: "Reddit" },
  { value: "email", label: "Email" },
  { value: "other", label: "Other" },
] as const;

type ManualDraftCreateResponse = {
  data: {
    briefId: string;
    campaignId: string;
    outputId: string;
  };
};

export function ManualDraftForm({
  brandId,
  onCancel,
  onCreated,
}: {
  brandId: string;
  onCancel?: () => void;
  onCreated?: (result: ManualDraftCreateResponse["data"]) => void;
}) {
  const queryClient = useQueryClient();
  const campaigns = useCampaignList(brandId);
  const campaignRef = useRef<HTMLSelectElement | null>(null);
  const surfaceRef = useRef<HTMLSelectElement | null>(null);
  const titleRef = useRef<HTMLInputElement | null>(null);
  const contentRef = useRef<HTMLTextAreaElement | null>(null);
  const [savedDraft, setSavedDraft] = useState<ManualDraftCreateResponse["data"] | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  async function handleSaveDraft() {
    const selectedSurface = (surfaceRef.current?.value ?? "linkedin_derivative") as (typeof SURFACE_OPTIONS)[number]["value"];
    const trimmedTitle = titleRef.current?.value.trim() ?? "";
    const trimmedContent = contentRef.current?.value.trim() ?? "";
    if (!trimmedTitle) {
      setSavedDraft(null);
      setErrorMessage("Title is required.");
      return;
    }
    if (!trimmedContent) {
      setSavedDraft(null);
      setErrorMessage("Content is required.");
      return;
    }

    const campaignId = campaignRef.current?.value;
    if (campaignId && !campaigns.data?.data.some(c => c.id === campaignId && c.brand_id === brandId && c.status === "active")) {
      setErrorMessage("Choose an active campaign belonging to this brand."); return;
    }
    setIsSaving(true);
    setErrorMessage(null);
    try {
      const result = await api.post<ManualDraftCreateResponse>(
        `/api/v1/brands/${encodeURIComponent(brandId)}/content`,
        {
          title: trimmedTitle,
          content: trimmedContent,
          channel: selectedSurface === "other" ? "other" : selectedSurface,
          content_type: selectedSurface === "blog" ? "article" : "post",
          ...(campaignId ? { campaign_id: campaignId } : {}),
        },
      );
      setSavedDraft(result.data);
      if (titleRef.current) titleRef.current.value = "";
      if (contentRef.current) contentRef.current.value = "";
      if (surfaceRef.current) surfaceRef.current.value = "linkedin_derivative";
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["brand-review", brandId] }),
        queryClient.invalidateQueries({ queryKey: ["campaigns", brandId] }),
      ]);
      onCreated?.(result.data);
    } catch (error) {
      setSavedDraft(null);
      setErrorMessage(error instanceof Error ? error.message : "Draft save failed.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <section className="rounded-[20px] border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-950">Write a manual draft</p>
          <p className="mt-1 text-sm text-slate-600">
            Save directly into Review when the agent is unavailable or you want to draft by hand.
          </p>
        </div>
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            Close
          </button>
        ) : null}
      </div>

      <label className="mt-4 block space-y-1.5 text-sm text-slate-700">
        <span className="font-medium">Campaign</span>
        <select ref={campaignRef} disabled={campaigns.isLoading} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2">
          <option value="">Ad-hoc draft (no existing campaign)</option>
          {(campaigns.data?.data ?? []).filter(c => c.brand_id === brandId && c.status === "active").map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select>
        {campaigns.isError && <p role="alert">Could not load campaigns. Retry loading before choosing an existing campaign.</p>}
      </label>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <label className="space-y-1.5 text-sm text-slate-700">
          <span className="font-medium">Surface</span>
          <select
            ref={surfaceRef}
            defaultValue="linkedin_derivative"
            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none"
          >
            {SURFACE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label className="space-y-1.5 text-sm text-slate-700">
          <span className="font-medium">Title</span>
          <input
            ref={titleRef}
            placeholder="Launch post"
            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 outline-none"
          />
        </label>
      </div>

      <label className="mt-4 block space-y-1.5 text-sm text-slate-700">
        <span className="font-medium">Content</span>
        <textarea
          ref={contentRef}
          placeholder="Draft the post, email, or note you want reviewed."
          className="min-h-[220px] w-full rounded-[18px] border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none"
        />
      </label>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => {
            void handleSaveDraft();
          }}
          disabled={isSaving}
          className="rounded-full bg-slate-950 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSaving ? "Saving draft…" : "Save draft to Review"}
        </button>
        <Link
          to={buildBrandReviewPath(brandId)}
          className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Open Review
        </Link>
      </div>

      {savedDraft ? (
        <p className="mt-3 text-sm text-emerald-700">
          Draft saved. <Link to={buildBrandReviewPath(brandId, `briefId=${encodeURIComponent(savedDraft.briefId)}`)} className="font-medium underline">View in Review</Link>
        </p>
      ) : null}

      {errorMessage ? <p className="mt-3 text-sm text-red-700">{errorMessage}</p> : null}
    </section>
  );
}
